/**
 * The island grid: terrain, glyphs, coordinates and the seen bitset.
 *
 * A tile is a `Terrain` and nothing else. Relics and ruins are recorded
 * separately by the generator -- a relic sits *on* terrain and does not change
 * its movement cost (`docs/rules.md` section 2) -- so nothing here needs to
 * know whether a tile has been staked.
 *
 * Coordinates are `x` across (0..19) and `y` down (0..11), which is the
 * numbering letters quote and the interface prints in its margins. A tile is
 * addressed either as `{ x, y }` or as its row-major index, and `index` and
 * `position` are the only two places that conversion happens.
 */

import { GAME_CONFIG, MOVEMENT_COST, type Terrain } from './game-config.ts'

const { width, height } = GAME_CONFIG.grid

/** Every terrain, in the order the legend prints them. */
export const TERRAINS = ['water', 'sand', 'grass', 'forest', 'mountain', 'ruin'] as const

/** A tile on the grid. */
export type Position = { readonly x: number; readonly y: number }

/** Tiles in the grid: 240, one bit each in `player_islands.seen_bits`. */
export const TILE_COUNT = width * height

/**
 * The map glyphs, from the design prototype's legend. The interface draws them
 * in a CSS grid with fixed cell widths rather than a `<pre>`, because these
 * fall back across fonts (`AGENTS.md`, design).
 */
export const TERRAIN_GLYPH = {
  water: '~',
  sand: '.',
  grass: ',',
  forest: '♠',
  mountain: '▲',
  ruin: '⌂',
} as const satisfies Record<Terrain, string>

/** A relic nobody has staked. */
export const RELIC_GLYPH = '◆'
/** A tile the player has no claim about: their own fog, or an unfilled slice cell. */
export const UNKNOWN_GLYPH = '·'

/**
 * Glyphs a map slice may contain. A slice is a claim, so it may say anything
 * the map can say -- including "I saw a relic here" and "I do not know" -- and
 * `validateSlice` checks legality, never truth.
 */
export const SLICE_GLYPHS: readonly string[] = [
  ...Object.values(TERRAIN_GLYPH),
  RELIC_GLYPH,
  UNKNOWN_GLYPH,
]

const GLYPH_TERRAIN = new Map<string, Terrain>(
  Object.entries(TERRAIN_GLYPH).map(([terrain, glyph]) => [glyph, terrain as Terrain]),
)

/** The terrain a glyph claims, or `undefined` for the relic and unknown glyphs. */
export function terrainOfGlyph(glyph: string): Terrain | undefined {
  return GLYPH_TERRAIN.get(glyph)
}

/** Stamina to enter this terrain, or `null` when it cannot be entered. */
export function moveCost(terrain: Terrain): number | null {
  return MOVEMENT_COST[terrain]
}

export function isPassable(terrain: Terrain): boolean {
  return MOVEMENT_COST[terrain] !== null
}

// --- coordinates -----------------------------------------------------------

/**
 * The four directions a player may move. Diagonals exist as content -- a letter
 * can say "north-east" -- but not as a move, unless an ability adds them
 * (Q2.2, Q10.4).
 */
export const CARDINALS = {
  n: { x: 0, y: -1 },
  e: { x: 1, y: 0 },
  s: { x: 0, y: 1 },
  w: { x: -1, y: 0 },
} as const

export const DIAGONALS = {
  ne: { x: 1, y: -1 },
  se: { x: 1, y: 1 },
  sw: { x: -1, y: 1 },
  nw: { x: -1, y: -1 },
} as const

/** Direction ids, matching the `direction` slot values in the letter grammar. */
export const DIRECTIONS = { ...CARDINALS, ...DIAGONALS } as const
export type Direction = keyof typeof DIRECTIONS
export type Cardinal = keyof typeof CARDINALS

export function inBounds(position: Position): boolean {
  return (
    Number.isInteger(position.x) &&
    Number.isInteger(position.y) &&
    position.x >= 0 &&
    position.x < width &&
    position.y >= 0 &&
    position.y < height
  )
}

/** Row-major index of a position. Callers check `inBounds` first. */
export function index(position: Position): number {
  return position.y * width + position.x
}

export function position(tileIndex: number): Position {
  return { x: tileIndex % width, y: Math.floor(tileIndex / width) }
}

export function step(from: Position, direction: Direction): Position {
  const delta = DIRECTIONS[direction]
  return { x: from.x + delta.x, y: from.y + delta.y }
}

export function samePosition(a: Position, b: Position): boolean {
  return a.x === b.x && a.y === b.y
}

export function manhattan(a: Position, b: Position): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y)
}

/** Chebyshev distance, which is the shape sight uses: a square, not a diamond. */
export function chebyshev(a: Position, b: Position): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y))
}

/** The orthogonal neighbours inside the grid. */
export function neighbours(from: Position): Position[] {
  return Object.keys(CARDINALS)
    .map((direction) => step(from, direction as Cardinal))
    .filter(inBounds)
}

/** Every tile within `radius` in Chebyshev distance, inside the grid. */
export function square(centre: Position, radius: number): Position[] {
  const tiles: Position[] = []
  for (let y = centre.y - radius; y <= centre.y + radius; y += 1) {
    for (let x = centre.x - radius; x <= centre.x + radius; x += 1) {
      const candidate = { x, y }
      if (inBounds(candidate)) tiles.push(candidate)
    }
  }
  return tiles
}

// --- the seen bitset -------------------------------------------------------

/**
 * One bit per tile, stored as a 30-byte blob in `player_islands.seen_bits`.
 *
 * A bitset rather than a list of coordinates because it is fixed-width, cheap
 * to union for guild completion (`docs/rules.md` section 8) and cannot grow
 * with how much a player walks.
 */
export type TileSet = Uint8Array

export const TILE_SET_BYTES = Math.ceil(TILE_COUNT / 8)

export function createTileSet(): TileSet {
  return new Uint8Array(TILE_SET_BYTES)
}

export function hasTile(set: TileSet, position_: Position): boolean {
  const bit = index(position_)
  return (set[bit >> 3]! & (1 << (bit & 7))) !== 0
}

/** Adds a tile. Returns true when it was not already in the set. */
export function addTile(set: TileSet, position_: Position): boolean {
  const bit = index(position_)
  const byte = bit >> 3
  const mask = 1 << (bit & 7)
  if ((set[byte]! & mask) !== 0) return false
  set[byte] = set[byte]! | mask
  return true
}

export function tileSetSize(set: TileSet): number {
  let total = 0
  for (const byte of set) {
    let bits = byte
    while (bits !== 0) {
      bits &= bits - 1
      total += 1
    }
  }
  return total
}

/** Every tile in the set, in row-major order. */
export function tileSetPositions(set: TileSet): Position[] {
  const found: Position[] = []
  for (let tile = 0; tile < TILE_COUNT; tile += 1) {
    const candidate = position(tile)
    if (hasTile(set, candidate)) found.push(candidate)
  }
  return found
}

/** A new set holding every tile in either. Guild completion is a union of these. */
export function unionTileSets(sets: readonly TileSet[]): TileSet {
  const union = createTileSet()
  for (const set of sets) {
    for (let byte = 0; byte < TILE_SET_BYTES; byte += 1) {
      union[byte] = union[byte]! | (set[byte] ?? 0)
    }
  }
  return union
}

export function copyTileSet(set: TileSet): TileSet {
  return Uint8Array.from(set)
}
